import { useCallback, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import {
  splitPath,
  stripLanguagePrefix,
  withLanguagePrefix,
} from '@/lib/languagePath';

/**
 * Hook that handles language-aware routing
 * Extracts language from URL path and updates i18n accordingly.
 *
 * The path rules live in lib/languagePath.ts: the 14 site languages prefix
 * every path; a page language (Thai, Traditional Chinese:
 * shared/pageLanguages.ts) prefixes only the pages it has, so on a Thai page
 * withLanguagePath("/pricing") is "/pricing".
 */
export function useLanguageRouter() {
  const [location, setLocation] = useLocation();
  const { i18n } = useTranslation();

  const { pathname, search, hash } = splitPath(location);
  const { lang: detectedLang, pathname: strippedPathname } = stripLanguagePrefix(pathname);

  const currentLang = detectedLang || 'en';
  const pathWithoutLang = `${strippedPathname}${search}${hash}` || '/';

  const withLanguagePath = useCallback(
    (path: string, lang = currentLang) => withLanguagePrefix(path, lang),
    [currentLang],
  );

  // Update i18n language when path language changes
  useEffect(() => {
    if (i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  /**
   * Navigate to a path with current language prefix
   */
  const navigate = useCallback((path: string, lang?: string) => {
    const targetLang = lang || currentLang;
    const fullPath = withLanguagePath(path, targetLang);
    setLocation(fullPath);
  }, [currentLang, setLocation, withLanguagePath]);

  /**
   * Change language and navigate to same path in new language
   */
  const changeLanguage = useCallback((newLang: string) => {
    const newPath = withLanguagePath(pathWithoutLang, newLang);
    setLocation(newPath);
  }, [pathWithoutLang, setLocation, withLanguagePath]);

  return {
    currentLang,
    pathWithoutLang,
    location,
    navigate,
    changeLanguage,
    withLanguagePath,
  };
}
